import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../context/AuthContext";
import { useSocket } from "../context/SocketContext";
import { useTranslation } from "../context/TranslationContext";
import {
  getSupportThreads,
  getMySupportThread,
  startSupportThread,
} from "../api/mintSpace";
import { conversationPreview } from "../utils/groupChats";
import { getProfileAvatarUrl } from "../utils/userDisplay";
import ChatWindow from "./ChatWindow";
import Avatar from "./ds/Avatar";
import Badge from "./ds/Badge";
import SearchField from "./ds/SearchField";

const MintSpaceSupportHub = ({
  isDark,
  setToast,
  mintSpace,
  isOwner,
  isModerator,
  onSupportUnreadChange,
}) => {
  const { user } = useAuth();
  const { socket } = useSocket();
  const { t } = useTranslation();
  const staff = !!(isOwner || isModerator);

  const [loading, setLoading] = useState(true);
  const [threads, setThreads] = useState([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [selected, setSelected] = useState(null);
  const [memberThread, setMemberThread] = useState(null);
  const [starting, setStarting] = useState(false);
  const [search, setSearch] = useState("");
  const [showResolved, setShowResolved] = useState(false);

  const refreshStaff = useCallback(async () => {
    if (!mintSpace?._id) return;
    try {
      const res = await getSupportThreads(mintSpace._id);
      if (res.success && res.data) {
        const list = res.data.threads || [];
        const tu = typeof res.data.totalUnread === "number" ? res.data.totalUnread : 0;
        setThreads(list);
        setTotalUnread(tu);
        if (typeof onSupportUnreadChange === "function") onSupportUnreadChange(tu);
      }
    } catch (e) {
      console.error(e);
    }
  }, [mintSpace?._id, onSupportUnreadChange]);

  const refreshMember = useCallback(async () => {
    if (!mintSpace?._id) return;
    try {
      const res = await getMySupportThread(mintSpace._id);
      if (res.success) setMemberThread(res.data);
    } catch (e) {
      console.error(e);
    }
  }, [mintSpace?._id]);

  useEffect(() => {
    if (!mintSpace?._id || !user) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      if (staff) await refreshStaff();
      else await refreshMember();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [mintSpace?._id, user, staff, refreshStaff, refreshMember]);

  useEffect(() => {
    if (!socket || !staff || !mintSpace?._id) return;
    socket.emit("join_mintspace_staff", { mintSpaceId: mintSpace._id });
    const bump = () => {
      refreshStaff();
    };
    socket.on("support_new_message", bump);
    return () => {
      socket.off("support_new_message", bump);
    };
  }, [socket, staff, mintSpace?._id, refreshStaff]);

  const handleStartMember = async () => {
    if (!mintSpace?._id) return;
    setStarting(true);
    try {
      const res = await startSupportThread(mintSpace._id);
      if (res.success && res.data) {
        setMemberThread(res.data);
        if (setToast) {
          setToast(t("mintSpace.supportChatReady") || "Support chat is open");
        }
      }
    } catch (e) {
      if (setToast) setToast(e?.message || t("mintSpace.supportStartFailed") || "Could not start support");
    } finally {
      setStarting(false);
    }
  };

  const baseText = "text-label-primary";
  const mutedText = "text-label-secondary";
  const cardBase = "border-separator-default bg-bg-raised";

  if (loading) {
    return (
      <div className={`flex items-center justify-center min-h-[320px] rounded-radius-md border ${cardBase}`}>
        <div className={`text-subheadline ${mutedText}`}>{t("common.loading") || "Loading..."}</div>
      </div>
    );
  }

  if (!staff) {
    return (
      <div className="w-full max-w-4xl mx-auto space-y-4">
        {!memberThread ? (
          <div className={`rounded-radius-md border p-8 ${cardBase}`}>
            <h3 className={`text-title-2 mb-2 ${baseText}`}>
              {t("mintSpace.contactSupportTitle") || "Contact support"}
            </h3>
            <p className={`text-subheadline mb-6 ${mutedText}`}>
              {t("mintSpace.contactSupportBody") ||
                "Start a conversation with the space owner. You will get read receipts and replies here."}
            </p>
            <button
              type="button"
              onClick={handleStartMember}
              disabled={starting}
              className="rounded-radius-sm bg-accent-default px-6 py-3 font-semibold text-accent-on disabled:opacity-50"
            >
              {starting
                ? t("common.loading") || "Loading..."
                : t("mintSpace.startSupportChat") || "Start support chat"}
            </button>
          </div>
        ) : (
          <div className="min-h-[480px]">
            <ChatWindow
              conversation={memberThread}
              currentUser={user}
              isDark={isDark}
              setToast={setToast}
              skipBlockCheck
              mintSpaceId={mintSpace._id}
              onSupportConversationUpdated={(conv) => setMemberThread(conv)}
              onRestartSupport={handleStartMember}
            />
          </div>
        )}
      </div>
    );
  }

  const peerFor = (thread) =>
    thread.requesterProfile ||
    thread.participants?.find(
      (p) => (p._id || p)?.toString() === (thread.requester?._id || thread.requester)?.toString()
    ) ||
    null;

  const openThreads = threads.filter((thread) => thread.status !== "ended");
  const resolvedThreads = threads.filter((thread) => thread.status === "ended");
  const needle = search.trim().toLowerCase();
  const shownThreads = (showResolved ? resolvedThreads : openThreads).filter((thread) => {
    const peer = peerFor(thread);
    return !needle || (peer?.username || peer?.businessName || "").toLowerCase().includes(needle);
  });
  const chip = (label, active, onClick) => (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`h-9 rounded-full px-space-16 text-footnote-semibold transition-colors ${
        active
          ? "bg-accent-tinted text-accent-tinted-label shadow-[inset_0_0_0_1px_var(--color-accent-tinted-label)]"
          : "bg-fill-secondary text-label-secondary"
      }`}
    >
      {label}
    </button>
  );

  return (
    <div className="flex w-full flex-col gap-space-24 font-ds lg:h-[calc(100vh-112px)] lg:min-h-[520px] lg:flex-row">
      {/* Figma C11: the inbox beside the open conversation */}
      <aside className="flex max-h-[70vh] w-full shrink-0 flex-col gap-space-12 rounded-radius-lg bg-bg-raised p-space-16 lg:max-h-none lg:w-[340px]">
        <div className="flex items-center gap-space-8">
          <h3 className="min-w-0 flex-1 truncate text-title-3 text-label-primary">
            {t("mintSpace.supportInbox") || "Support inbox"}
          </h3>
          {totalUnread > 0 && <Badge tone="warning">{t("mintSpace.supportNewCount", { count: totalUnread })}</Badge>}
        </div>
        <SearchField
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={t("mintSpace.supportSearch")}
        />
        <div className="flex flex-wrap gap-space-8">
          {chip(`${t("mintSpace.supportOpen")} · ${openThreads.length}`, !showResolved, () => setShowResolved(false))}
          {chip(t("mintSpace.supportResolved"), showResolved, () => setShowResolved(true))}
        </div>
        <div className="flex min-h-0 flex-1 flex-col gap-space-4 overflow-y-auto">
          {shownThreads.length === 0 ? (
            <p className="p-space-12 text-subheadline text-label-secondary">
              {t("mintSpace.noSupportThreads") || "No conversations yet"}
            </p>
          ) : (
            shownThreads.map((thread) => {
              const peer = peerFor(thread);
              const name = peer?.username || peer?.businessName || t("common.user") || "User";
              const active = selected?._id === thread._id;
              return (
                <button
                  key={thread._id}
                  type="button"
                  onClick={() => setSelected(thread)}
                  className={`flex w-full items-center gap-space-12 rounded-[20px] px-space-12 py-space-8 text-left transition-colors ${
                    active ? "bg-control-selected" : "hover:bg-fill-quaternary"
                  }`}
                >
                  <Avatar size="l" src={getProfileAvatarUrl(peer)} name={name} />
                  <span className="flex min-w-0 flex-1 flex-col gap-space-4">
                    <span className="truncate text-subheadline-semibold text-label-primary">{name}</span>
                    {thread.lastMessage && (
                      <span className="truncate text-footnote text-label-secondary">
                        {conversationPreview(thread, user?.id ?? user?._id, t)}
                      </span>
                    )}
                  </span>
                  {(thread.unreadCount || 0) > 0 && (
                    <Badge size="s" tone="danger" className="min-w-[20px]">
                      {thread.unreadCount}
                    </Badge>
                  )}
                </button>
              );
            })
          )}
        </div>
      </aside>
      <section className="min-h-[480px] min-w-0 flex-1">
        {selected ? (
          <ChatWindow
            key={selected._id}
            fill
            conversation={selected}
            currentUser={user}
            isDark={isDark}
            setToast={setToast}
            skipBlockCheck
            peerUserOverride={peerFor(selected)}
            mintSpaceId={mintSpace._id}
            onSupportConversationUpdated={(conv) => {
              setSelected(conv);
              refreshStaff();
            }}
          />
        ) : (
          <div className="flex h-full min-h-[400px] items-center justify-center rounded-radius-lg bg-bg-raised">
            <p className="text-subheadline text-label-secondary">
              {t("mintSpace.selectSupportThread") || "Select a conversation"}
            </p>
          </div>
        )}
      </section>
    </div>
  );
};

export default MintSpaceSupportHub;
